import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ChevronLeft, Shield, Lock, CheckCircle, Users, Key, Activity, AlertCircle, Loader, ExternalLink } from 'lucide-react';
import { apiClient } from '@/api/axios';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';
import { AssignProfessionalModal, type AssignedProfessional } from './AssignProfessionalModal';

interface DIDCreationProps {
  onClose: () => void;
  petData: {
    id: string;
    name: string;
    species: string;
    breed: string;
    country: string;
  };
  onComplete: (did: string) => void;
}

interface PermissionSettings {
  viewData: string[];
  amendFields: string[];
  issueCredentials: string[];
  timeBound: boolean;
  expiryDate: string;
}

export function DIDCreation({ onClose, petData, onComplete }: DIDCreationProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [generatedDID, setGeneratedDID] = useState('');
  const [allowProfessionalCredentials, setAllowProfessionalCredentials] = useState(true);
  const [emergencyAccess, setEmergencyAccess] = useState(true);
  const [permissions, setPermissions] = useState<PermissionSettings>({
    viewData: ['veterinarians', 'groomers'],
    amendFields: ['veterinarians'],
    issueCredentials: ['veterinarians'],
    timeBound: false,
    expiryDate: ''
  });
  const [assignedProfessionals, setAssignedProfessionals] = useState<Record<string, AssignedProfessional[]>>({
    veterinary: [],
    grooming: [],
    training: [],
    other: [],
  });
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignModalType, setAssignModalType] = useState<'veterinary' | 'grooming' | 'training' | 'other'>('veterinary');

  const handleOpenAssignModal = (type: 'veterinary' | 'grooming' | 'training' | 'other') => {
    setAssignModalType(type);
    setAssignModalOpen(true);
  };

  const handleAssignProfessional = (prof: AssignedProfessional) => {
    setAssignedProfessionals((prev) => ({
      ...prev,
      [prof.type]: [...(prev[prof.type] || []), prof],
    }));
  };

  const handleRemoveProfessional = (type: string, id: string) => {
    setAssignedProfessionals((prev) => ({
      ...prev,
      [type]: (prev[type] || []).filter((p) => p.id !== id),
    }));
  };

  const totalSteps = 5;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleGenerateDID = async () => {
    // Move to step 3 first to show the generation UI
    setCurrentStep(3);
    setIsGenerating(true);
    
    try {
      // Call backend API to create DID on Solana blockchain
      // The backend uses the Petso wallet to create the DID - users don't need crypto
      const response = await apiClient.patch(`/pets/${petData.id}/did`);
      const did = response.data.pet.did;
      setGeneratedDID(did);
    } catch (error) {
      console.error('Failed to create DID on-chain:', error);
      // Show error to user - they can retry later
      setGeneratedDID(''); // Clear any previous DID
    } finally {
      setIsGenerating(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    
    // Save DID permissions before completing
    try {
      await apiClient.put(`/pets/${petData.id}/did/permissions`, {
        viewData: permissions.viewData,
        amendFields: permissions.amendFields,
        issueCredentials: permissions.issueCredentials,
        timeBound: permissions.timeBound,
        expiryDate: permissions.timeBound ? permissions.expiryDate : null,
      });
    } catch (error) {
      console.error('Failed to save DID permissions:', error);
      // Continue anyway - user can update permissions later
    } finally {
      setIsCompleting(false);
    }
    
    onComplete(generatedDID);
    onClose();
  };

  const steps = [
    t('didCreation.stepExplanation'),
    t('didCreation.stepWallet'),
    t('didCreation.stepGenerate'),
    t('didCreation.stepPermissions'),
    t('didCreation.stepConfirmation'),
  ];

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 sm:p-6">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <div>
              <h2 className="text-lg sm:text-2xl mb-1">{t('didCreation.createPetDigitalId')}</h2>
              <p className="text-sm opacity-90">
                {t('didCreation.creatingDigitalIdentityFor', { name: petData.name })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base transition-colors ${
                      index + 1 < currentStep
                        ? 'bg-green-500'
                        : index + 1 === currentStep
                        ? 'bg-white text-purple-600'
                        : 'bg-white/30'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <span className={index + 1 === currentStep ? '' : 'text-white'}>
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <span className="text-xs mt-1 sm:mt-2 text-center hidden sm:block">{step}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index + 1 < currentStep ? 'bg-green-500' : 'bg-white/30'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          {currentStep === 1 && <Step1Explanation />}
          {currentStep === 2 && (
            <Step2WalletAuthority
              allowProfessionalCredentials={allowProfessionalCredentials}
              setAllowProfessionalCredentials={setAllowProfessionalCredentials}
              emergencyAccess={emergencyAccess}
              setEmergencyAccess={setEmergencyAccess}
              assignedProfessionals={assignedProfessionals}
              onOpenAssignModal={handleOpenAssignModal}
              onRemoveProfessional={handleRemoveProfessional}
            />
          )}
          {currentStep === 3 && (
            <Step3GenerateDID
              petData={petData}
              isGenerating={isGenerating}
              generatedDID={generatedDID}
              onGenerate={handleGenerateDID}
            />
          )}
          {currentStep === 4 && (
            <Step4Permissions
              permissions={permissions}
              setPermissions={setPermissions}
            />
          )}
          {currentStep === 5 && (
            <Step5Confirmation
              petData={petData}
              generatedDID={generatedDID}
              permissions={permissions}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3 sm:p-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-xs sm:text-sm text-gray-500">
              {t('didCreation.stepOf', { current: currentStep, total: totalSteps })}
            </div>

            <div className="flex gap-2 sm:gap-3">
              {currentStep > 1 && currentStep < 3 && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm sm:text-base"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('didCreation.back')}
                </button>
              )}

              {currentStep === 1 && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  {t('didCreation.continue')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}

              {currentStep === 2 && (
                <button
                  onClick={handleGenerateDID}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  {t('didCreation.generateDigitalId')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}

              {currentStep === 3 && generatedDID && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  {t('didCreation.continueToPermissions')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}

              {currentStep === 4 && (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  {t('didCreation.continue')}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}

              {currentStep === 5 && (
                <button
                  onClick={handleComplete}
                  disabled={isCompleting}
                  className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-70 text-sm sm:text-base"
                >
                  {isCompleting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      {t('didCreation.saving')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t('didCreation.completeSetup')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <AssignProfessionalModal
      isOpen={assignModalOpen}
      onClose={() => setAssignModalOpen(false)}
      professionalType={assignModalType}
      professionalLabel={
        { veterinary: 'Veterinarian', grooming: 'Groomer', training: 'Trainer', other: 'Professional' }[assignModalType]
      }
      petId={petData.id}
      onAssign={handleAssignProfessional}
    />
    </>
  );
}

// Step 1: Explain What a Pet Digital ID Is
function Step1Explanation() {
  const { t } = useTranslation();
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-10 h-10 text-purple-600" />
        </div>
        <h3 className="text-xl sm:text-2xl mb-2">{t('didCreation.whatIsPetDigitalId')}</h3>
        <p className="text-gray-600">
          {t('didCreation.digitalPassportDesc')}
        </p>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-3 sm:p-6">
        <h4 className="mb-3 flex items-center gap-2">
          <span className="text-2xl">🔒</span>
          {t('didCreation.simpleExplanation')}
        </h4>
        <p className="text-gray-700 leading-relaxed">
          {t('didCreation.keepsSafeDesc')}
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
          <li>{t('didCreation.basicDetails')}</li>
          <li>{t('didCreation.healthRecords')}</li>
          <li>{t('didCreation.documents')}</li>
          <li>{t('didCreation.ownershipInformation')}</li>
        </ul>
        <p className="text-gray-700 leading-relaxed mt-3">
          {t('didCreation.youDecideWhoCanSee')}
        </p>
        <p className="text-gray-700 leading-relaxed mt-2">
          {t('didCreation.lostTravelDesc')}
        </p>
      </div>

      {/* Benefits */}
      <div>
        <h4 className="mb-4">{t('didCreation.keyBenefits')}</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="mb-1">{t('didCreation.securityAndTrust')}</div>
                <div className="text-sm text-gray-600">
                  {t('didCreation.securityAndTrustDesc')}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="mb-1">{t('didCreation.fullControl')}</div>
                <div className="text-sm text-gray-600">
                  {t('didCreation.fullControlDesc')}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="mb-1">{t('didCreation.portability')}</div>
                <div className="text-sm text-gray-600">
                  {t('didCreation.portabilityDesc')}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="mb-1">{t('didCreation.immutableHistory')}</div>
                <div className="text-sm text-gray-600">
                  {t('didCreation.immutableHistoryDesc')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* On-chain vs Off-chain */}
      <div>
        <h4 className="mb-4">{t('didCreation.whatsStoredWhere')}</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-purple-600" />
              <span>{t('didCreation.onSolanaBlockchain')}</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('didCreation.petDigitalIdIdentifier')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('didCreation.ownershipRecords')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('didCreation.credentialHashes')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('didCreation.permissionSettings')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600">•</span>
                <span>{t('didCreation.transactionHistory')}</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
              <span>{t('didCreation.encryptedOffChain')}</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>{t('didCreation.medicalRecordsDocuments')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>{t('didCreation.photosAndVideos')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>{t('didCreation.detailedHealthNotes')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>{t('didCreation.behavioralInformation')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600">•</span>
                <span>{t('didCreation.personalOwnerNotes')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
        <div className="text-sm text-yellow-800">
          <strong>{t('didCreation.privacyFirst')}</strong> {t('didCreation.privacyFirstDesc')}
        </div>
      </div>
    </div>
  );
}

// Step 2: Wallet & Authority Assignment
function Step2WalletAuthority({ allowProfessionalCredentials, setAllowProfessionalCredentials, emergencyAccess, setEmergencyAccess, assignedProfessionals, onOpenAssignModal, onRemoveProfessional }: any) {
  const { t } = useTranslation();
  const categories: Array<{ key: 'veterinary' | 'grooming' | 'training' | 'other'; label: string; singular: string; buttonLabel: string }> = [
    { key: 'veterinary', label: t('didCreation.veterinarians'), singular: 'veterinarian', buttonLabel: t('didCreation.assignVet') },
    { key: 'grooming', label: t('didCreation.groomers'), singular: 'groomer', buttonLabel: t('didCreation.assignGroomer') },
    { key: 'training', label: t('didCreation.trainers'), singular: 'trainer', buttonLabel: t('didCreation.assignTrainer') },
    { key: 'other', label: t('didCreation.otherProfessionals'), singular: 'professional', buttonLabel: t('didCreation.assignProfessional') },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl mb-2">{t('didCreation.walletAndAuthority')}</h3>
        <p className="text-gray-600">{t('didCreation.configureAuthority')}</p>
      </div>

      {/* Wallet Type Selection */}
      <div>
        <h4 className="mb-3">{t('didCreation.walletManagement')}</h4>
        <div className="space-y-3">
          <div className="p-4 border-2 border-purple-600 bg-purple-50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <div className="mb-1">{t('didCreation.petsoCustodialWallet')}</div>
                <div className="text-sm text-gray-600">
                  {t('didCreation.custodialWalletDesc')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Roles */}
      <div>
        <h4 className="mb-3">{t('didCreation.professionalAuthorityAssignment')}</h4>
        <p className="text-sm text-gray-600 mb-4">
          {t('didCreation.optionallyAssignRoles')}
        </p>

        <div className="space-y-3">
          {categories.map((cat) => {
            const assigned = assignedProfessionals?.[cat.key] || [];
            return (
              <div key={cat.key} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-600" />
                    <span>{cat.label}</span>
                  </div>
                  <button
                    onClick={() => onOpenAssignModal(cat.key)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    {cat.buttonLabel}
                  </button>
                </div>

                {assigned.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('didCreation.noAssignedYet', { label: cat.label.toLowerCase() })}</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {assigned.map((prof: any) => (
                      <div
                        key={prof.id}
                        className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{prof.name}</p>
                            <p className="text-xs text-gray-500">
                              {prof.source === 'existing' ? t('didCreation.petsoPro') : t('didCreation.invited')}
                              {prof.email ? ` · ${prof.email}` : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => onRemoveProfessional(cat.key, prof.id)}
                          className="text-xs text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                        >
                          {t('didCreation.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* User Controls */}
      <div>
        <h4 className="mb-3">{t('didCreation.authorityControls')}</h4>
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowProfessionalCredentials}
              onChange={(e) => setAllowProfessionalCredentials(e.target.checked)}
              className="w-5 h-5 text-purple-600 rounded mt-0.5"
            />
            <div>
              <div className="mb-1">{t('didCreation.allowProfessionalCredentials')}</div>
              <div className="text-sm text-gray-600">
                {t('didCreation.allowProfessionalCredentialsDesc')}
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={emergencyAccess}
              onChange={(e) => setEmergencyAccess(e.target.checked)}
              className="w-5 h-5 text-purple-600 rounded mt-0.5"
            />
            <div>
              <div className="mb-1">{t('didCreation.emergencyAccessFallback')}</div>
              <div className="text-sm text-gray-600">
                {t('didCreation.emergencyAccessFallbackDesc')}
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          {t('didCreation.modifyProfessionalAssignments')}
        </div>
      </div>
    </div>
  );
}

// Step 3: Generate Pet DID
function Step3GenerateDID({ petData, isGenerating, generatedDID, onGenerate }: any) {
  const { t } = useTranslation();
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <h3 className="text-xl sm:text-2xl mb-2">{t('didCreation.generatePetDigitalId')}</h3>
        <p className="text-gray-600">{t('didCreation.createUniqueIdentityOnSolana')}</p>
      </div>

      {!isGenerating && !generatedDID && (
        <>
          {/* Pet Summary */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 sm:p-6">
            <h4 className="mb-4">{t('didCreation.petInformationSummary')}</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">{t('didCreation.petName')}</div>
                <div>{petData.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t('didCreation.species')}</div>
                <div className="capitalize">{petData.species}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t('didCreation.breed')}</div>
                <div>{petData.breed}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">{t('didCreation.petId')}</div>
                <div className="font-mono text-sm">{petData.id}</div>
              </div>
            </div>
          </div>

          {/* What Will Happen */}
          <div>
            <h4 className="mb-3">{t('didCreation.whatWillHappen')}</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  1
                </div>
                <div>
                  <div className="mb-1">{t('didCreation.generateUniqueDigitalId')}</div>
                  <div className="text-sm text-gray-600">
                    {t('didCreation.uniqueSolanaIdentifier', { name: petData.name })}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  2
                </div>
                <div>
                  <div className="mb-1">{t('didCreation.hashCoreIdentity')}</div>
                  <div className="text-sm text-gray-600">
                    {t('didCreation.hashCoreIdentityDesc')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  3
                </div>
                <div>
                  <div className="mb-1">{t('didCreation.registerOnSolana')}</div>
                  <div className="text-sm text-gray-600">
                    {t('didCreation.registerOnSolanaDesc')}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  4
                </div>
                <div>
                  <div className="mb-1">{t('didCreation.linkToPetsoProfile')}</div>
                  <div className="text-sm text-gray-600">
                    {t('didCreation.linkToPetsoProfileDesc')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={onGenerate}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-opacity text-base sm:text-lg"
            >
              {t('didCreation.generateDigitalIdOnSolana')}
            </button>
          </div>
        </>
      )}

      {isGenerating && (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader className="w-10 h-10 text-purple-600 animate-spin" />
          </div>
          <h4 className="text-xl mb-4">{t('didCreation.creatingDigitalIdOnSolana')}</h4>
          <div className="max-w-md mx-auto space-y-3">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span>{t('didCreation.generatingUniqueIdentifier')}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span>{t('didCreation.hashingPetIdentityData')}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <Loader className="w-6 h-6 text-purple-600 animate-spin" />
              <span>{t('didCreation.submittingTransactionToSolana')}</span>
            </div>
          </div>
        </div>
      )}

      {generatedDID && !isGenerating && (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h4 className="text-xl mb-2">{t('didCreation.digitalIdSuccessfullyCreated')}</h4>
          <p className="text-gray-600 mb-6">{t('didCreation.petDigitalIdentityOnSolana')}</p>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 sm:p-6 max-w-2xl mx-auto">
            <div className="text-sm text-gray-600 mb-2">{t('didCreation.petDigitalId')}</div>
            <div className="font-mono text-lg break-all bg-white rounded p-3 border border-purple-200">
              {generatedDID}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Step 4: Digital ID Permissions Layer
function Step4Permissions({ permissions, setPermissions }: any) {
  const professionalRoles = [
    { id: 'veterinarians', label: 'Veterinarians', icon: '🏥' },
    { id: 'groomers', label: 'Groomers', icon: '✂️' },
    { id: 'trainers', label: 'Trainers', icon: '🎓' },
    { id: 'boarders', label: 'Boarding Facilities', icon: '🏠' },
    { id: 'emergency', label: 'Emergency Services', icon: '🚨' }
  ];

  const togglePermission = (type: keyof PermissionSettings, role: string) => {
    setPermissions((prev: PermissionSettings) => {
      const current = prev[type] as string[];
      if (current.includes(role)) {
        return { ...prev, [type]: current.filter((r: string) => r !== role) };
      } else {
        return { ...prev, [type]: [...current, role] };
      }
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl mb-2">Digital ID Permissions Layer</h3>
        <p className="text-gray-600">Define granular access controls for your pet's data</p>
      </div>

      {/* Who Can View Data */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          Who can view data?
        </h4>
        <div className="space-y-2">
          {professionalRoles.map(role => (
            <label key={role.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.viewData.includes(role.id)}
                onChange={() => togglePermission('viewData', role.id)}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="text-xl">{role.icon}</span>
              <span>{role.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Who Can Amend Fields */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-orange-600" />
          Who can amend specific fields?
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          These roles can update certain information (e.g., vets can update medical records)
        </p>
        <div className="space-y-2">
          {professionalRoles.map(role => (
            <label key={role.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.amendFields.includes(role.id)}
                onChange={() => togglePermission('amendFields', role.id)}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="text-xl">{role.icon}</span>
              <span>{role.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Who Can Issue Credentials */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Who can issue verifiable credentials?
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          These roles can issue blockchain-verified certificates (vaccination records, training certificates, etc.)
        </p>
        <div className="space-y-2">
          {professionalRoles.map(role => (
            <label key={role.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={permissions.issueCredentials.includes(role.id)}
                onChange={() => togglePermission('issueCredentials', role.id)}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="text-xl">{role.icon}</span>
              <span>{role.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Time-Bound Permissions */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4">Time-Bound Permissions (Optional)</h4>
        <label className="flex items-start gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={permissions.timeBound}
            onChange={(e) => setPermissions((prev: PermissionSettings) => ({ ...prev, timeBound: e.target.checked }))}
            className="w-5 h-5 text-purple-600 rounded mt-0.5"
          />
          <div>
            <div className="mb-1">Set expiry date for all permissions</div>
            <div className="text-sm text-gray-600">
              Automatically revoke all professional access after a specific date
            </div>
          </div>
        </label>

        {permissions.timeBound && (
          <div className="ml-8">
            <label className="block text-sm text-gray-600 mb-2">Expiry Date</label>
            <input
              type="date"
              value={permissions.expiryDate}
              onChange={(e) => setPermissions((prev: PermissionSettings) => ({ ...prev, expiryDate: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        )}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
        <div className="text-sm text-green-800">
          <strong>Fully Revocable:</strong> You can change or revoke any permission at any time from your pet's Digital ID dashboard. 
          All changes are recorded on the blockchain.
        </div>
      </div>
    </div>
  );
}

// Step 5: Confirmation & Digital ID Dashboard
function Step5Confirmation({ petData, generatedDID, permissions }: any) {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-xl sm:text-2xl mb-2">Digital ID Successfully Created!</h3>
        <p className="text-gray-600">
          {petData.name}'s digital identity is now active on Solana
        </p>
      </div>

      {/* Digital ID Information */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-md p-3 sm:p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6" />
          <h4 className="text-xl">Pet Digital ID</h4>
        </div>
        <div className="bg-white/10 rounded-lg p-4 mb-4">
          <div className="font-mono text-sm break-all">{generatedDID}</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="flex-1 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors">
            Copy Digital ID
          </button>
          {/* Solana Explorer Link */}
          {generatedDID && generatedDID.startsWith('did:petso:') && (
            <a
              href={`https://explorer.solana.com/address/${generatedDID.replace('did:petso:', '')}${getSolanaClusterParam()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View on Solana
            </a>
          )}
        </div>
      </div>

      {/* Verification Status */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-600">Verification Status</span>
          </div>
          <div className="text-xl">Verified</div>
          <div className="text-xs text-gray-500 mt-1">On Solana blockchain</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-gray-600">Linked Professionals</span>
          </div>
          <div className="text-xl">0</div>
          <div className="text-xs text-gray-500 mt-1">Assign from dashboard</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-gray-600">Credentials</span>
          </div>
          <div className="text-xl">0</div>
          <div className="text-xs text-gray-500 mt-1">Add records below</div>
        </div>
      </div>

      {/* Permissions Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <h4 className="mb-4">Active Permissions</h4>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-600 mb-2">Can View Data</div>
            <div className="space-y-1">
              {permissions.viewData.length > 0 ? (
                permissions.viewData.map((role: string) => (
                  <div key={role} className="text-sm capitalize">{role}</div>
                ))
              ) : (
                <div className="text-sm text-gray-400">None</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-2">Can Amend Fields</div>
            <div className="space-y-1">
              {permissions.amendFields.length > 0 ? (
                permissions.amendFields.map((role: string) => (
                  <div key={role} className="text-sm capitalize">{role}</div>
                ))
              ) : (
                <div className="text-sm text-gray-400">None</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600 mb-2">Can Issue Credentials</div>
            <div className="space-y-1">
              {permissions.issueCredentials.length > 0 ? (
                permissions.issueCredentials.map((role: string) => (
                  <div key={role} className="text-sm capitalize">{role}</div>
                ))
              ) : (
                <div className="text-sm text-gray-400">None</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-gray-600" />
          <h4>Activity Log (Immutable)</h4>
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full mt-2" />
            <div className="flex-1">
              <div className="text-sm">Digital ID Created</div>
              <div className="text-xs text-gray-500">Just now • Registered on Solana</div>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
            <div className="flex-1">
              <div className="text-sm">Permissions Configured</div>
              <div className="text-xs text-gray-500">Just now • {permissions.viewData.length} roles granted view access</div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps - informational only, not clickable */}
      <div>
        <h4 className="mb-4">Next Steps</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="text-left">
              <div className="mb-1 font-medium text-gray-800">Attach Verified Records</div>
              <div className="text-sm text-gray-600">Upload vaccination records, health certificates</div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="text-left">
              <div className="mb-1 font-medium text-gray-800">Manage Permissions</div>
              <div className="text-sm text-gray-600">Assign professionals and adjust access</div>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">You can do these from your pet's profile after closing this setup.</p>
      </div>
    </div>
  );
}